import { useEffect, useRef, useState } from 'react';
import { Select, Space, Button, Tooltip } from 'antd';
import { PlusOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { AffaireService } from '../../services/AffaireService';
import { catchError } from '../../services/DaoService';
import Toast from '../../helpers/Toast';
import PropTypes from 'prop-types';
import styles from './AffaireLienInlineCreator.module.scss';

// Libellés directionnels proposés à l'utilisateur. Quand `swap` est vrai, le
// lien est stocké dans le sens inverse (le dossier choisi devient la source).
const LINK_TYPES = [
  { value: 'lie_a', label: 'Lié à', baseType: 'lie_a', swap: false },
  { value: 'bloque', label: 'Bloque', baseType: 'bloque', swap: false },
  { value: 'bloque_par', label: 'Bloqué par', baseType: 'bloque', swap: true },
  { value: 'duplique', label: 'Duplique', baseType: 'duplique', swap: false },
  { value: 'duplique_par', label: 'Dupliqué par', baseType: 'duplique', swap: true },
  { value: 'cause', label: 'Cause', baseType: 'cause', swap: false },
  { value: 'cause_par', label: 'Causé par', baseType: 'cause', swap: true },
];

// Détecte un lien de dossier collé (ex: "https://.../affaire/{uid}", copié
// via le bouton "Copier le lien") pour résoudre/sélectionner directement le
// dossier correspondant, comme JIRA le fait pour ses liens d'issues.
const AFFAIRE_LINK_REGEX = /\/affaire\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

export default function AffaireLienInlineCreator({ affaire, onCreated }) {
  const [active, setActive] = useState(false);
  const [type, setType] = useState('lie_a');
  const [targetId, setTargetId] = useState(null);
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!active) { setType('lie_a'); setTargetId(null); setOptions([]); }
  }, [active]);

  const resolvePastedLink = (text) => {
    const match = text.match(AFFAIRE_LINK_REGEX);
    if (!match) return false;

    setSearching(true);
    AffaireService.getInstance()
      .searchForLink(null, affaire.id, match[1])
      .then(r => {
        if (r.data.length === 0) {
          Toast.error("Ce dossier n'a pas été trouvé ou vous n'y avez pas accès.");
          setOptions([]);
          return;
        }
        const found = r.data[0];
        setOptions([{ value: found.id, label: `[${found.code}] ${found.nom}` }]);
        setTargetId(found.id);
      })
      .catch(catchError)
      .finally(() => setSearching(false));

    return true;
  };

  const handleSearch = (text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!text || text.length < 2) { setOptions([]); return; }

    if (resolvePastedLink(text)) return;

    setSearching(true);
    debounceRef.current = setTimeout(() => {
      AffaireService.getInstance()
        .searchForLink(text, affaire.id)
        .then(r => setOptions(r.data.map(a => ({ value: a.id, label: `[${a.code}] ${a.nom}` }))))
        .catch(catchError)
        .finally(() => setSearching(false));
    }, 300);
  };

  const reset = () => setActive(false);

  const submit = () => {
    if (!targetId) return;
    const linkType = LINK_TYPES.find(t => t.value === type);
    setSubmitting(true);
    AffaireService.getInstance()
      .createLien({
        affaire_id: affaire.id,
        affaire_source_id: linkType.swap ? targetId : affaire.id,
        affaire_cible_id: linkType.swap ? affaire.id : targetId,
        type: linkType.baseType,
      })
      .then((response) => {
        Toast.success(response.message);
        onCreated?.(response.data);
        reset();
      })
      .catch(catchError)
      .finally(() => setSubmitting(false));
  };

  if (!active) {
    return (
      <div className={styles.trigger} onClick={() => setActive(true)}>
        <PlusOutlined className={styles.triggerIcon} />
        <span>Ajouter un lien</span>
      </div>
    );
  }

  return (
    <div className={styles.row}>
      <Space.Compact className={styles.compact}>
        <Select
          value={type}
          onChange={setType}
          className={styles.typeSelect}
          options={LINK_TYPES.map(t => ({ value: t.value, label: t.label }))}
        />
        <Select
          showSearch
          autoFocus
          value={targetId}
          onChange={setTargetId}
          filterOption={false}
          placeholder="Code, nom ou lien du dossier (coller un lien le sélectionne directement)"
          notFoundContent={searching ? 'Recherche...' : 'Aucun dossier trouvé'}
          onSearch={handleSearch}
          options={options}
          className={styles.targetSelect}
          onKeyDown={(e) => { if (e.key === 'Escape') reset(); }}
        />
      </Space.Compact>
      <Tooltip title="Ajouter">
        <Button type="text" size="small" icon={<CheckOutlined />} loading={submitting} disabled={!targetId} onClick={submit} className={styles.confirmIcon} />
      </Tooltip>
      <Tooltip title="Annuler">
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={reset} className={styles.cancelIcon} />
      </Tooltip>
    </div>
  );
}

AffaireLienInlineCreator.propTypes = {
  affaire: PropTypes.object.isRequired,
  onCreated: PropTypes.func,
};
